import React, { useCallback, useContext, useEffect, useState } from "react";
import _ from "lodash";
import eventEmitter from "../../shared/events";
import classnames from "classnames";

import AttributeList from "./attribute-list";
import AttributeErrors from "./attribute-errors";
import CompareHeader from "./compare-table/compare-header";
import Loader from "../loader/loader";
import { TaskContext } from "../../contexts/task";
import { useReferences } from "./references/hooks/useReferences";

const DualCodingAttributeTable = ({
    readOnlyOverride,
    showValidation,
    showReferences,
    taskType,
    isQa,
    roleLabels,
    group,
    groupsFetched,
    isGroupsLoading,
    refetchCoderRoles,
    refetchCoderGroups,
    hasRedo,
    setHasRedo,
    isReadOnlyAdd,
    isReadOnlyEdit,
    isReadOnlyDelete,
    isRefreshing,
    setRefresh,
    coderTypeLabel,
    isBrandVerified,
    roleDefinitions,
    refetchAttributeRoles,
    isAttributeRolesLoading,
}) => {
    const [columnWidths, setColumnWidth] = useState({});
    const { task } = useContext(TaskContext);

    const [definitions, setDefinitions] = useState({});
    const { references, selectReferences, toggleModalSelect, selectStyles } = useReferences();

    const handleRefresh = () => setRefresh(true);

    useEffect(() => {
        eventEmitter.on("REFRESH_ATTRIBUTE_DEFINITIONS", handleRefresh);

        if (refetchCoderRoles) {
            eventEmitter.on("REFRESH_ATTRIBUTE_VALUES", refetchCoderRoles);
        }

        return () => {
            eventEmitter.removeListener("REFRESH_ATTRIBUTE_DEFINITIONS", handleRefresh);

            if (refetchCoderRoles) {
                eventEmitter.removeListener("REFRESH_ATTRIBUTE_VALUES", refetchCoderRoles);
            }
        };
    }, []);

    useEffect(() => {
        if (roleDefinitions) {
            return setDefinitions(roleDefinitions);
        }

        if (group && groupsFetched) {
            return setDefinitions(group);
        }
    }, [roleDefinitions, group, groupsFetched]);

    useEffect(() => {
        if (!isRefreshing) {
            return;
        }

        setRefresh(false);

        if (group) {
            refetchCoderGroups();
        }

        if (roleDefinitions) {
            refetchAttributeRoles();
        }
    }, [isRefreshing]);

    useEffect(() => {
        if (references.refresh) {
            handleRefresh();
            references.refresh = false;
        }
    }, [references.refresh]);

    const handleColumnWidthChange = useCallback((col, width) => {
        setColumnWidth((state) => ({ ...state, [col]: width }));
    }, []);

    const handleInitialColSizes = useCallback(() => setColumnWidth({}), []);

    if (isAttributeRolesLoading || isGroupsLoading) {
        return (
            <div className="txt-center">
                <Loader natural={true} />
            </div>
        );
    }

    return (
        <>
            <div className={classnames("attribute-table", { "-remove-position": isQa ? true : false })}>
                {!isQa && selectReferences}
                <div style={selectStyles}>
                    <CompareHeader
                        isQa={isQa}
                        columnWidths={columnWidths}
                        referenceProducts={showReferences ? references.activeProducts : []}
                        toggleModalSelect={toggleModalSelect}
                        hideSelect={!showReferences}
                        onColumnWidthChange={handleColumnWidthChange}
                        onInitColSizes={handleInitialColSizes}
                        roleLabels={roleLabels}
                        hasRedo={hasRedo}
                        setHasRedo={setHasRedo}
                        refetchCoderRoles={refetchCoderRoles}
                        coderTypeLabel={coderTypeLabel}
                        isBrandVerified={isBrandVerified}
                    />
                    <AttributeErrors
                        attributeGroups={definitions.groups}
                        attributesByGroup={definitions.byGroup}
                        readOnlyOverride={readOnlyOverride}
                        showValidation={showValidation}
                        allowNoAttributes={_.isEmpty(definitions)}
                    />
                    <AttributeList
                        columnWidths={columnWidths}
                        isCompleted={task.isCompleted}
                        attributeGroups={definitions.groups}
                        attributesByGroup={definitions.byGroup}
                        attributesSubgroups={definitions.subGroups}
                        references={showReferences ? references.activeProducts : []}
                        referenceKeys={references.activeProductKeys}
                        readOnlyOverride={readOnlyOverride}
                        taskType={taskType}
                        roleLabels={roleLabels}
                        refetchCoderRoles={refetchCoderRoles}
                        hasRedo={hasRedo}
                        isReadOnlyAdd={isReadOnlyAdd}
                        isReadOnlyEdit={isReadOnlyEdit}
                        isReadOnlyDelete={isReadOnlyDelete}
                        roleTaskType={task.taskType}
                        isBrandVerified={isBrandVerified}
                        isQa={isQa}
                    />
                </div>
            </div>
        </>
    );
};

DualCodingAttributeTable.defaultProps = {
    fetchDefinitions: null,
    showValidation: true,
    hideReferences: false,
};

export default DualCodingAttributeTable;
